import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { ConnectObjectType } from "../../constants";
import { CreateEditMetricForm, ICreateEditMetricFormState } from ".";
import { useForm } from "react-hook-form";
import { MetricDto, useEditMetric } from "../../http";
import { useEffect } from "react";
import { DateTime } from "luxon";

interface IProps {
  isVisible: boolean;
  metric: MetricDto;
  onUpdated: () => void;
  onCancelled: () => void;
}

const EditMetricModal = (props: IProps) => {
  const { mutate, isLoading: isSaving } = useEditMetric();

  const form = useForm<ICreateEditMetricFormState>({
    defaultValues: {
      title: props.metric?.title ?? "",
      description: props.metric?.description ?? "",
      assignedTeam: props.metric?.assignedTeam ?? null,
      reportingUser: props.metric?.reportingUser ?? null,
      sortOrder: props.metric?.sortOrder ?? "",
      datasets: [...props.metric?.datasets]
        .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
        .map((dataset) => {
          return {
            guid: dataset.guid,
            title: dataset.title,
            displayAsBar: dataset.displayAsBar,
            dotted: dataset.dotted,
            color: dataset.color,
            data: dataset.data.map((data) => {
              return {
                rowGuid: data.rowGuid,
                date: DateTime.fromISO(data.date, { zone: "utc" }),
                value: data.value,
              };
            }),
            sortOrder: dataset.sortOrder,
          };
        }),
      annotations: props.metric?.annotations ?? [],
    },
  });

  useEffect(() => {
    form.reset({
      title: props.metric?.title ?? "",
      description: props.metric?.description ?? "",
      assignedTeam: props.metric?.assignedTeam ?? null,
      reportingUser: props.metric?.reportingUser ?? null,
      sortOrder: props.metric?.sortOrder ?? "",
      datasets: [...props.metric?.datasets]
        .sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))
        .map((dataset) => {
          return {
            guid: dataset.guid,
            title: dataset.title,
            displayAsBar: dataset.displayAsBar,
            dotted: dataset.dotted,
            color: dataset.color,
            sortOrder: dataset.sortOrder,
            data: dataset.data.map((data) => {
              return {
                rowGuid: data.rowGuid,
                date: DateTime.fromISO(data.date, { zone: "utc" }),
                value: data.value,
              };
            }),
          };
        }),
      annotations: props.metric?.annotations ?? [],
    });
  }, [props.metric, form]);

  async function handleUpdateClicked() {
    if (props.metric === undefined) {
      return;
    }

    form.handleSubmit((formState) => {
      mutate(
        {
          guid: props.metric!.guid,
          title: formState.title,
          description: formState.description,
          assignedTeamSlug: formState.assignedTeam?.slug,
          reportingUserGuid: formState.reportingUser?.userId,
          sortOrder: formState.sortOrder,
          datasets: formState.datasets?.map((dataset) => {
            return {
              guid: dataset.guid,
              title: dataset.title,
              displayAsBar: dataset.displayAsBar,
              dotted: dataset.dotted,
              color: dataset.color,
              data: dataset.data.map((data) => {
                return {
                  rowGuid: data.rowGuid,
                  date: data.date.toString(),
                  value: data.value,
                };
              }),
              sortOrder: dataset.sortOrder,
            };
          }),
          annotations: formState.annotations
            .filter((annotation) => annotation.text !== null)
            .map((annotation) => {
              return {
                rowGuid: annotation.rowGuid,
                text: annotation.text!,
              };
            }),
        },
        {
          onSuccess: () => {
            props.onUpdated();
          },
        }
      );
    })();
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    form.reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  return (
    <Dialog open={props.isVisible} onClose={handleClose}>
      <DialogTitle>Editing {ConnectObjectType.Metric}</DialogTitle>
      <DialogContent sx={{ mb: 1 }}>
        <Stack spacing={1} sx={{ alignItems: "baseline" }}>
          <CreateEditMetricForm form={form} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isSaving} onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isSaving}
          endIcon={isSaving ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
          onClick={handleUpdateClicked}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { EditMetricModal };
